import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, CheckCircle2, Clock, ShieldAlert } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";
import { useAdvertiser } from "./useAdvertiser";

export default function VerificationPage() {
  useSeo({ title: "Verification | LUX", ...PRIVATE_SEO });
  const { me, refetchMe, invalidate } = useAdvertiser();
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const verif = me?.verification || {};
  const hasVerification = !!verif.verified;

  const start = async () => {
    setBusy("start");
    setError("");
    try {
      const { data } = await api.post("/advertiser/verification/start", { kind: hasVerification ? "reverify" : "initial" });
      setSession(data);
      toast.message("Mock session started", { description: "Use the buttons below to simulate a provider decision." });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  const complete = async (outcome) => {
    if (!session) return;
    setBusy(outcome);
    setError("");
    try {
      await api.post("/advertiser/verification/mock-complete", { provider_ref: session.provider_ref, outcome });
      toast.success(outcome === "approve" ? "Verification approved" : "Verification outcome recorded");
      setSession(null);
      invalidate();
      await refetchMe();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-6" data-testid="verification-page">
      <header>
        <p className="lux-label">Identity &amp; age</p>
        <h1 className="mt-2 font-serif text-4xl text-lux-text sm:text-5xl">Verification</h1>
        <p className="mt-3 max-w-2xl text-sm text-lux-muted">
          Advertisers must verify an ID document and a live selfie before publishing, and re-verify with a selfie every 90 days. In this prototype the provider is <strong className="text-lux-silverBright">mocked</strong>: no documents or selfies are collected. We only store the result, date, provider reference and that you are over 18.
        </p>
      </header>

      <section className="lux-card p-5 sm:p-6" data-testid="verification-current">
        <div className="flex items-start gap-3">
          <BadgeCheck className={`mt-1 h-5 w-5 shrink-0 ${hasVerification ? "text-lux-success" : "text-lux-silver"}`} aria-hidden="true" />
          <div>
            <p className="font-serif text-xl text-lux-text">
              {hasVerification ? (verif.overdue ? "Re-verification due" : "Identity verified") : "Not verified yet"}
            </p>
            {hasVerification ? (
              <p className="mt-1 text-sm text-lux-muted">
                Last verified <strong className="text-lux-silverBright">{fmtDate(verif.verified_at)}</strong> · provider reference <code className="font-mono text-lux-silver">{verif.provider_ref || "—"}</code>.
              </p>
            ) : (
              <p className="mt-1 text-sm text-lux-muted">Complete verification once to start publishing. You will re-verify every 90 days.</p>
            )}
          </div>
        </div>
      </section>

      {!session ? (
        <section className="lux-card p-5 sm:p-6">
          <p className="lux-label">Mock session</p>
          <h2 className="mt-2 font-serif text-2xl text-lux-text">Start a verification session</h2>
          <p className="mt-2 text-sm text-lux-muted">The real provider would open a camera flow on your phone. Here, we just create a reference and let you choose an outcome.</p>
          <button onClick={start} disabled={busy === "start"} className="lux-btn lux-btn-primary mt-5" data-testid="verification-start-button">
            {busy === "start" ? "Starting..." : hasVerification ? "Start re-verification" : "Start verification"}
          </button>
          {error && <p className="mt-3 text-sm text-lux-error" role="alert">{error}</p>}
        </section>
      ) : (
        <section className="lux-card p-5 sm:p-6" data-testid="verification-session">
          <div className="flex items-start gap-3">
            <Clock className="mt-1 h-5 w-5 shrink-0 text-lux-warning" aria-hidden="true" />
            <div className="flex-1">
              <p className="font-serif text-xl text-lux-text">Mock provider session</p>
              <p className="mt-1 text-sm text-lux-muted">Session reference <code className="font-mono text-lux-silver" data-testid="verification-provider-ref">{session.provider_ref}</code></p>
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <button onClick={() => complete("approve")} disabled={!!busy} className="lux-btn lux-btn-primary" data-testid="verification-mock-approve-button">
                  {busy === "approve" ? "Approving..." : "Approve (18+)"}
                </button>
                <button onClick={() => complete("under18")} disabled={!!busy} className="lux-btn lux-btn-secondary" data-testid="verification-mock-under18-button">
                  Simulate under 18
                </button>
                <button onClick={() => complete("reject")} disabled={!!busy} className="lux-btn lux-btn-secondary" data-testid="verification-mock-reject-button">
                  Simulate reject
                </button>
              </div>
              {error && <p className="mt-3 text-sm text-lux-error" role="alert">{error}</p>}
            </div>
          </div>
        </section>
      )}

      {hasVerification && !verif.overdue && (
        <section className="lux-card flex items-start gap-3 p-5 sm:p-6" data-testid="verification-success-note">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-lux-success" aria-hidden="true" />
          <div>
            <p className="font-serif text-lg text-lux-text">You're all set</p>
            <p className="mt-1 text-sm text-lux-muted">
              You can now create or update your listing. <Link to="/advertiser/listing" className="lux-link">Open the listing editor</Link>.
            </p>
          </div>
        </section>
      )}

      <section className="flex items-start gap-3 rounded-[12px] border border-lux-border bg-lux-surface2 p-5" data-testid="verification-privacy-note">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-lux-silver" aria-hidden="true" />
        <p className="text-sm text-lux-muted">
          Privacy: LUX never receives or stores your document images or selfie. We keep only the result, the date, the provider reference and whether you are over 18.
        </p>
      </section>
    </div>
  );
}
